<template>
  <div>
    <div class="schema-title" v-if="title && schema.properties">
      <vs-heading type="heading">{{ title }}</vs-heading>
    </div>
    <custom-fields-fetch-progress-indicator
      v-if="isPending || error"
      name="custom"
      :error-fetching-extension-schema="error"
      @retry="refetch"
    />
    <schema-field
      v-if="!error && !!schema.properties"
      :error-schema="errorSchema"
      :form-data="customData"
      :form-resolver="contextualizedFormResolver"
      :params="{ headingType: 'heading' }"
      :root-schema="schemaData"
      :schema="schema"
      :ui-schema="uiSchema"
      @change="handleUpdateCustomData"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch, toRef } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import CustomFieldsFetchProgressIndicator from '@/components/tickets/CustomFields/CustomFieldsFetchProgressIndicator.vue'
import { FormContext } from '@/utils/forms/form-context'
import { ContextualizedFormResolver } from '@/utils/forms/contextualized-form-resolver'
import { resolveFormDataForValidation } from '@/utils/forms/form-resolver'
import { validateFormData } from '@/utils/forms/form-validate'
import { useSyncTargetCustomSchema } from '@/api/schemas'
import { isEmpty } from 'lodash'
import CurrentUserContext from '@/models/current-user-context'
import VsHeading from '@/components/vision/VsHeading.vue'

const props = defineProps<{
  appId: string
  schemaVersionId: number
  value: Record<string, any> | null
  title?: string | undefined
}>()

const appIdRef = toRef(props, 'appId')
const schemaVersionIdRef = toRef(props, 'schemaVersionId')

const emit = defineEmits<{
  (e: 'dirty', value: boolean): void
}>()

const clone = (obj) => {
  return JSON.parse(JSON.stringify(obj))
}

const customData = ref(clone(props.value || {}))
const errorSchema = ref({})
const dirty = ref(false)

watch(
  () => props.value,
  (newModel, _) => {
    customData.value = clone(newModel)
  },
  { deep: true },
)

const {
  data: schemaData,
  isPending,
  error,
  refetch,
} = useSyncTargetCustomSchema({ appId: appIdRef, schemaId: schemaVersionIdRef })

const schema = computed(() => schemaData.value?.json_schema.schema ?? {})
const uiSchema = computed(() => schemaData.value?.json_schema.uiSchema ?? {})

const store = useStore()

const getCurrentUserContext = computed<CurrentUserContext>(
  () => store.getters['permissions/getContext'],
)

const contextualizedFormResolver = computed(() => {
  const currentUserContext = getCurrentUserContext.value
  const currentUser = {
    id: currentUserContext?.userSid,
    name: currentUserContext?.name,
  }

  const context = new FormContext({
    currentUser,
  })
  return new ContextualizedFormResolver(context)
})

const handleUpdateCustomData = (value) => {
  customData.value = value
  if (!dirty.value && hasChanges()) {
    dirty.value = true
    emit('dirty', true)
  }
}

const save = () => {
  return { data: customData.value, schemaId: props.schemaVersionId }
}

const validate = () => {
  const resolvedSchema = contextualizedFormResolver.value.retrieveSchema(
    schema,
    schema,
    customData.value,
  )
  const resolvedData = resolveFormDataForValidation(customData.value)

  errorSchema.value = validateFormData(resolvedSchema.value, resolvedData)

  return isEmpty(errorSchema.value)
}

const hasChanges = () => {
  return JSON.stringify(props.value) !== JSON.stringify(customData.value)
}

interface ExposedMethods {
  validate: () => boolean
  save: () => { data: object; schemaId: number }
}

defineExpose<ExposedMethods>({
  validate,
  save,
})
</script>

<style scoped>
.schema-title {
  margin-bottom: 1rem;
}
</style>
